import { FrameSpeed } from 'reducers';

const DEFAULT_TIMELINE_FRAME_RATE = FrameSpeed.Usual as number;

export function getTimelineFrameRate(dataFrameRate?: number | null): number {
    if (Number.isFinite(dataFrameRate) && Number(dataFrameRate) > 0) {
        return Number(dataFrameRate);
    }

    return DEFAULT_TIMELINE_FRAME_RATE;
}

export function getPlaybackRateMultiplier(frameSpeed: number): number {
    return frameSpeed / DEFAULT_TIMELINE_FRAME_RATE;
}

export function getPlaybackFrameRate(dataFrameRate: number | null | undefined, frameSpeed: number): number {
    return getTimelineFrameRate(dataFrameRate) * getPlaybackRateMultiplier(frameSpeed);
}

export function frameToRelativeSeconds(frame: number, startFrame: number, frameRate: number): number {
    return Math.max(0, frame - startFrame) / frameRate;
}

export function secondsToFrame(
    seconds: number,
    startFrame: number,
    stopFrame: number,
    frameRate: number,
    rounding: 'ceil' | 'floor' = 'ceil',
): number {
    const clampedSeconds = Math.max(0, seconds);
    const offset = rounding === 'floor'
        ? Math.floor(clampedSeconds * frameRate)
        : Math.ceil(clampedSeconds * frameRate);

    return Math.min(stopFrame, Math.max(startFrame, startFrame + offset));
}

export function getFramesDurationSeconds(startFrame: number, stopFrame: number, frameRate: number): number {
    return Math.max(1, stopFrame - startFrame + 1) / frameRate;
}

export function getRationalFps(fps: number): { fpsNum: number; fpsDen: number; isStable: boolean } {
    if (!Number.isFinite(fps) || fps <= 0) {
        return { fpsNum: 15, fpsDen: 1, isStable: false };
    }
    // Check common standard ones first to ensure exact matches:
    const rounded = Math.round(fps * 1000) / 1000;
    if (Math.abs(fps - 29.97) < 0.005 || Math.abs(fps - 29.9700299) < 0.0001 || rounded === 29.97) {
        return { fpsNum: 30000, fpsDen: 1001, isStable: true };
    }
    if (Math.abs(fps - 23.976) < 0.005 || Math.abs(fps - 23.9760239) < 0.0001 || rounded === 23.976) {
        return { fpsNum: 24000, fpsDen: 1001, isStable: true };
    }
    if (Math.abs(fps - 59.94) < 0.005 || Math.abs(fps - 59.9400599) < 0.0001 || rounded === 59.94) {
        return { fpsNum: 60000, fpsDen: 1001, isStable: true };
    }
    if (Math.abs(fps - 30) < 0.001) {
        return { fpsNum: 30, fpsDen: 1, isStable: true };
    }
    if (Math.abs(fps - 24) < 0.001) {
        return { fpsNum: 24, fpsDen: 1, isStable: true };
    }
    if (Math.abs(fps - 25) < 0.001) {
        return { fpsNum: 25, fpsDen: 1, isStable: true };
    }
    if (Math.abs(fps - 60) < 0.001) {
        return { fpsNum: 60, fpsDen: 1, isStable: true };
    }
    if (Math.abs(fps - 15) < 0.001) {
        return { fpsNum: 15, fpsDen: 1, isStable: true };
    }

    if (Math.abs(fps - Math.round(fps)) < 0.001) {
        return { fpsNum: Math.round(fps), fpsDen: 1, isStable: true };
    }
    const scaled = Math.round(fps * 1000);
    return { fpsNum: scaled, fpsDen: 1000, isStable: false };
}

export function msToFrame(
    targetMs: number,
    startFrame: number,
    stopFrame: number,
    fpsNum: number,
    fpsDen: number
): number {
    const scale = fpsDen * 1000;
    const roundingOffset = scale - 1;
    const offset = Math.floor((targetMs * fpsNum + roundingOffset) / scale);
    return Math.min(stopFrame, Math.max(startFrame, startFrame + offset));
}

export function frameToMs(
    frame: number,
    startFrame: number,
    fpsNum: number,
    fpsDen: number
): number {
    const relativeFrame = Math.max(0, frame - startFrame);
    return Math.floor((relativeFrame * fpsDen * 1000) / fpsNum);
}

